/** `comments.author_id` for replies the assistant posts; it has no auth user behind it. */
export const ASSISTANT_AUTHOR_ID = 'assistant';

export const ASSISTANT_DISPLAY_NAME = 'Assistant';

// The '@' is required: "the AI was wrong" is talk about the assistant, "@AI" is talk to it.
const MENTION_PATTERN = /(?<![\w#@])@(assistant|ai)(?![\w#])/i;

export function detectAssistantMention(text: string): boolean {
	return MENTION_PATTERN.test(text);
}

export function isAssistantAuthor(authorId: string | null | undefined): boolean {
	return authorId === ASSISTANT_AUTHOR_ID;
}

/** Fields for a comment the sidebar assistant posts for the person chatting with it. */
export function assistantCommentFor(operatorId: string, chat?: AssistantChatRef | null) {
	return {
		authorId: ASSISTANT_AUTHOR_ID,
		metadata: { onBehalfOf: operatorId, ...(chat ? { assistantChat: chat } : {}) },
	};
}

/** The sidebar assistant chat (its users row id) and the turn in it that posted a comment. */
export interface AssistantChatRef {
	chatId: number;
	turnId: string;
}

export function assistantChatRefOf(value: unknown): AssistantChatRef | null {
	const { chatId, turnId } = (value ?? {}) as { chatId?: unknown; turnId?: unknown };
	if (typeof chatId !== 'number' || !Number.isInteger(chatId) || chatId <= 0) return null;
	return typeof turnId === 'string' && turnId ? { chatId, turnId } : null;
}

/** The person whose sidebar assistant posted the comment; null for anyone's own comment. */
export function commentOnBehalfOf(comment: { metadata?: Record<string, any> | null }): string | null {
	const id = comment.metadata?.onBehalfOf;
	return typeof id === 'string' ? id : null;
}

/** The assistant chat a comment came from, for the person who had that chat only; the chat is private to them. */
export function commentAssistantChatFor(
	comment: { metadata?: Record<string, any> | null },
	viewerId: string | null | undefined
): AssistantChatRef | null {
	if (!viewerId || commentOnBehalfOf(comment) !== viewerId) return null;
	return assistantChatRefOf(comment.metadata?.assistantChat);
}

/** The author may edit or delete a comment, and so may the person whose assistant posted it. */
export function canEditComment(
	comment: { authorId: string; metadata?: Record<string, any> | null },
	userId: string | null | undefined,
): boolean {
	return !!userId && (comment.authorId === userId || commentOnBehalfOf(comment) === userId);
}

// Matches the comments route's maxDuration; a placeholder older than this was orphaned by a crash.
const PENDING_REPLY_MAX_AGE_MS = 5 * 60 * 1000;

/** The assistant's "thinking" placeholder, posted once it has decided to reply and filled in when done. */
export function isPendingAssistantReply(comment: {
	authorId: string;
	metadata?: Record<string, any> | null;
}): boolean {
	return isAssistantAuthor(comment.authorId) && comment.metadata?.pending === true;
}

export function isStalePendingReply(comment: { createdAt: Date | string }, now = Date.now()): boolean {
	return now - new Date(comment.createdAt).getTime() > PENDING_REPLY_MAX_AGE_MS;
}
